import { useState, useEffect } from 'react';
import { adminApi, type AdminUser, type UserDetail } from '@/lib/admin';
import { useToast } from '@/components/base/Toast';
import { SectionCard, StatusBadge, EmptyState, LoadingState, ErrorState } from '@/pages/admin/components/UI';

const verifiedMaps = {
  true: { label: '已认证', className: 'bg-green-100 text-green-600', icon: 'ri-check-line' },
  false: { label: '未认证', className: 'bg-red-100 text-red-500', icon: 'ri-close-line' },
  default: { label: '未知', className: 'bg-brown-100 text-brown-600', icon: 'ri-question-line' },
};

const roleMaps = {
  admin: { label: '管理员', className: 'bg-gold-100 text-gold-700', icon: 'ri-admin-line' },
  user: { label: '用户', className: 'bg-brown-100 text-brown-600', icon: 'ri-user-line' },
  default: { label: '用户', className: 'bg-brown-100 text-brown-600', icon: 'ri-user-line' },
};

export default function AdminUsers() {
  const { showToast } = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [detailTarget, setDetailTarget] = useState<AdminUser | null>(null);
  const [detailData, setDetailData] = useState<UserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await adminApi.listUsers();
      setUsers(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleViewDetail = async (user: AdminUser) => {
    setDetailTarget(user);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const data = await adminApi.getUserDetail(user.id);
      setDetailData(data);
      setLastRefreshTime(new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : '加载失败';
      showToast(msg, 'error');
      console.error('getUserDetail error:', e);
      setDetailTarget(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApi.deleteUser(deleteTarget.id);
      showToast('用户已删除', 'success');
      setDeleteTarget(null);
      setDeleteConfirmText('');
      fetchData();
    } catch (e) {
      const msg = e instanceof Error ? e.message : '删除失败';
      showToast(msg, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // 每5分钟自动刷新用户详情数据（实时盈亏）
  useEffect(() => {
    if (!detailTarget) return;
    const timer = setInterval(() => {
      handleViewDetail(detailTarget);
    }, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [detailTarget]);

  const filtered = users.filter((u) => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return true;
    return (
      (u.phone || '').includes(kw) ||
      (u.name || '').includes(kw) ||
      (u.email || '').toLowerCase().includes(kw)
    );
  });

  const confirmKeyword = deleteTarget ? (deleteTarget.phone || '删除') : '';

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-brown-900 text-lg font-bold">用户管理</h2>
          <p className="text-brown-400 text-xs mt-0.5">共 {users.length} 位用户</p>
        </div>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-brown-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索手机号/姓名"
            className="w-full sm:w-64 pl-9 pr-3 py-2 rounded-lg border border-brown-200 bg-white text-sm text-brown-800 outline-none focus:ring-2 focus:ring-gold-400"
          />
        </div>
      </div>

      <SectionCard>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState onRetry={fetchData} />
        ) : filtered.length === 0 ? (
          <EmptyState title="暂无用户" desc="还没有匹配的用户" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brown-400 text-xs border-b border-brown-100">
                  <th className="px-4 md:px-5 py-3 font-medium">用户</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">认证</th>
                  <th className="px-4 py-3 font-medium">会员等级</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">余额</th>
                  <th className="px-4 py-3 font-medium">角色</th>
                  <th className="px-4 py-3 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-brown-50 last:border-0 hover:bg-brown-50/40 transition-colors">
                    <td className="px-4 md:px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0">
                          <span className="w-5 h-5 flex items-center justify-center text-gold-700">
                            <i className="ri-user-fill text-base"></i>
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-brown-900 font-medium truncate">
                            {u.name || '未填写姓名'}
                          </p>
                          <p className="text-brown-400 text-xs truncate">{u.phone || u.email || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <StatusBadge status={String(u.verified)} maps={verifiedMaps} />
                    </td>
                    <td className="px-4 py-3.5 text-brown-700">{u.member_level || '普通会员'}</td>
                    <td className="px-4 py-3.5 hidden sm:table-cell text-brown-700">
                      ¥{u.balance.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={u.role} maps={roleMaps} />
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetail(u)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gold-200 text-gold-600 text-xs cursor-pointer whitespace-nowrap hover:bg-gold-50 transition-colors"
                        >
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-eye-line text-xs"></i>
                          </span>
                          查看详情
                        </button>
                        {u.role !== 'admin' && (
                          <button
                            onClick={() => { setDeleteTarget(u); setDeleteConfirmText(''); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-red-200 text-red-500 text-xs cursor-pointer whitespace-nowrap hover:bg-red-50 transition-colors"
                          >
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-delete-bin-line text-xs"></i>
                            </span>
                            删除
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* 用户详情弹窗 */}
      {detailTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setDetailTarget(null); setDetailData(null); }}></div>
          <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto animate-fade-up">
            {detailLoading ? (
              <LoadingState />
            ) : detailData ? (
              <>
                {/* Header */}
                <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-5 py-4 border-b border-brown-100">
                  <h3 className="text-brown-900 text-base font-bold">用户详情</h3>
                  <div className="flex items-center gap-3">
                    {lastRefreshTime && (
                      <span className="text-brown-400 text-xs">更新于 {lastRefreshTime}</span>
                    )}
                    <button
                      onClick={() => { setDetailTarget(null); setDetailData(null); }}
                      className="w-8 h-8 flex items-center justify-center text-brown-400 hover:text-brown-600 cursor-pointer rounded-full hover:bg-brown-50 transition-colors"
                    >
                      <i className="ri-close-line text-lg"></i>
                    </button>
                  </div>
                </div>

                <div className="p-5 space-y-5">
                  {/* 用户基础信息 */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0">
                      <span className="w-6 h-6 flex items-center justify-center text-gold-700">
                        <i className="ri-user-fill text-lg"></i>
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-brown-900 font-bold text-base">
                        {detailData.profile.name || '未填写姓名'}
                      </p>
                      <p className="text-brown-400 text-xs mt-0.5">
                        {detailData.profile.phone || detailData.profile.email || '—'} · {detailData.profile.member_level || '普通会员'}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-brown-400 text-xs">账户余额</p>
                      <p className="text-brown-900 font-bold">
                        ¥{detailData.profile.balance.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>

                  {/* 盈亏汇总 — 5个卡片 */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className={`rounded-lg p-3 text-center border ${detailData.summary.total_profit > 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                      <p className={`text-[10px] mb-1 ${detailData.summary.total_profit > 0 ? 'text-emerald-600' : 'text-red-500'}`}>累计盈亏</p>
                      <p className={`text-lg font-bold ${detailData.summary.total_profit > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {detailData.summary.total_profit > 0 ? '-' : '+'}
                        ¥{Math.abs(detailData.summary.total_profit).toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="bg-gold-50 rounded-lg p-3 text-center border border-gold-100">
                      <p className="text-gold-600 text-[10px] mb-1">持仓克重</p>
                      <p className="text-brown-900 text-lg font-bold">
                        {detailData.summary.total_position_weight.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}g
                      </p>
                    </div>
                    <div className="bg-brown-50 rounded-lg p-3 text-center border border-brown-100">
                      <p className="text-brown-500 text-[10px] mb-1">净资产</p>
                      <p className="text-brown-900 text-lg font-bold">
                        ¥{detailData.summary.net_asset_value.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
                      <p className="text-green-600 text-[10px] mb-1">总入金</p>
                      <p className="text-brown-900 text-lg font-bold">
                        ¥{detailData.summary.total_deposit_amount.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
                      <p className="text-red-500 text-[10px] mb-1">总出金</p>
                      <p className="text-brown-900 text-lg font-bold">
                        ¥{detailData.summary.total_withdraw_amount.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>

                  {/* 持仓订单 */}
                  <div>
                    <h4 className="text-brown-900 text-sm font-bold mb-3 flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center text-gold-600">
                        <i className="ri-lock-line text-sm"></i>
                      </span>
                      持仓订单 ({detailData.positions.length})
                    </h4>
                    {detailData.positions.length === 0 ? (
                      <div className="bg-brown-50 rounded-lg p-4 text-center">
                        <p className="text-brown-400 text-xs">暂无持仓订单</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-brown-100">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-brown-400 text-xs bg-brown-50">
                              <th className="px-3 py-2 font-medium">品种</th>
                              <th className="px-3 py-2 font-medium">方向</th>
                              <th className="px-3 py-2 font-medium">克重</th>
                              <th className="px-3 py-2 font-medium">开仓价</th>
                              <th className="px-3 py-2 font-medium">当前价</th>
                              <th className="px-3 py-2 font-medium">实时盈亏</th>
                              <th className="px-3 py-2 font-medium">保证金</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailData.positions.map((p) => (
                              <tr key={p.id} className="border-t border-brown-50">
                                <td className="px-3 py-2.5 text-brown-900 font-medium">{p.name}</td>
                                <td className="px-3 py-2.5">
                                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${p.direction === 'IN' ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-600'}`}>
                                    {p.direction === 'IN' ? '买入' : '卖出'}
                                  </span>
                                </td>
                                <td className="px-3 py-2.5 text-brown-700">{p.weight}g</td>
                                <td className="px-3 py-2.5 text-brown-700">{p.open_price.toFixed(2)}</td>
                                <td className="px-3 py-2.5 text-brown-700">{p.current_price.toFixed(2)}</td>
                                <td className={`px-3 py-2.5 font-medium ${p.live_profit >= 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                                  {p.live_profit >= 0 ? '+' : ''}{p.live_profit.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-3 py-2.5 text-brown-700">
                                  ¥{p.original_margin.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* 实物资产 */}
                  {detailData.physical_assets.length > 0 && (
                    <div>
                      <h4 className="text-brown-900 text-sm font-bold mb-3 flex items-center gap-2">
                        <span className="w-4 h-4 flex items-center justify-center text-gold-600">
                          <i className="ri-vip-diamond-line text-sm"></i>
                        </span>
                        实物资产 ({detailData.physical_assets.length})
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {detailData.physical_assets.map((a) => (
                          <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-brown-100 bg-brown-50/30">
                            <div className="w-10 h-10 rounded-lg bg-gold-100 flex items-center justify-center flex-shrink-0">
                              <span className="w-5 h-5 flex items-center justify-center text-gold-700">
                                <i className="ri-vip-diamond-fill text-base"></i>
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-brown-900 text-sm font-medium">{a.product}</p>
                              <p className="text-brown-400 text-xs mt-0.5">{a.weight}g · {a.status}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-brown-400 text-[10px]">{a.location || '金库'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* 删除用户确认弹窗 */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => { if (!deleting) { setDeleteTarget(null); setDeleteConfirmText(''); } }}></div>
          <div className="relative bg-white rounded-xl w-full max-w-md animate-fade-up">
            <div className="px-5 py-4 border-b border-brown-100 flex items-center justify-between">
              <h3 className="text-base font-bold text-red-600">删除用户</h3>
              <button
                onClick={() => { if (!deleting) { setDeleteTarget(null); setDeleteConfirmText(''); } }}
                className="w-8 h-8 flex items-center justify-center text-brown-400 hover:text-brown-600 cursor-pointer rounded-full hover:bg-brown-50 transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-sm text-red-600 leading-relaxed">
                <p className="font-bold mb-1">此操作不可恢复！</p>
                <p>删除后，该用户的所有订单、持仓、实物资产、入金/出金记录、收货地址、银行卡等数据都将被一并清除，且无法找回。</p>
              </div>
              <div className="text-sm text-brown-700">
                <p>即将删除用户：</p>
                <p className="mt-1 font-bold text-brown-900">{deleteTarget.name || '未填写姓名'}</p>
                <p className="text-brown-400 text-xs mt-0.5">{deleteTarget.phone || deleteTarget.email || '—'}</p>
              </div>
              <div>
                <label className="text-xs text-brown-500 mb-1.5 block">
                  请输入 <span className="font-bold text-red-500">{confirmKeyword}</span> 以确认删除
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={`输入 ${confirmKeyword}`}
                  className="w-full px-3 py-2 rounded-lg border border-brown-200 bg-white text-sm text-brown-800 outline-none focus:ring-2 focus:ring-red-400"
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 rounded-lg border border-brown-200 text-brown-700 text-sm cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting || deleteConfirmText !== confirmKeyword}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {deleting ? '删除中…' : '确认删除'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}